import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/** Selections use the product's actual sale unit. All amounts remain integer cents. */
export function buildGripSelection(variants, pairIds, pairCount) {
  const invalid = (error, invalidIndex = 0) => ({ error, invalidIndex, items: [], variants: [], total: 0, compareTotal: 0, saving: 0 });
  if (![1, 2, 4].includes(pairCount)) return invalid('Choose a quantity of 1, 2 or 4.');
  if (!Array.isArray(variants) || !Array.isArray(pairIds) || pairIds.length < pairCount) return invalid('Choose a colour for every item.');
  const variantMap = new Map(variants.map((variant) => [String(variant.id), variant]));
  const grouped = new Map();
  const selected = [];
  let total = 0;
  let compareTotal = 0;
  for (let index = 0; index < pairCount; index += 1) {
    const id = String(pairIds[index]);
    const variant = variantMap.get(id);
    if (!variant || !/^\d+$/.test(id)) return invalid(`Choose a colour for item ${index + 1}.`, index);
    if (!variant.available) return invalid(`Item ${index + 1}: ${variant.colour || 'this colour'} is sold out. Choose another colour.`, index);
    if (!Number.isSafeInteger(variant.price) || variant.price < 0) return invalid('This price could not be loaded. Please refresh the page.', index);
    const comparison = Number(variant.compareAtPrice);
    total += variant.price;
    compareTotal += Number.isSafeInteger(comparison) && comparison > variant.price ? comparison : variant.price;
    selected.push(variant);
    grouped.set(id, (grouped.get(id) || 0) + 1);
  }
  return {
    error: null,
    invalidIndex: -1,
    items: Array.from(grouped, ([id, quantity]) => ({ id, quantity })),
    variants: selected,
    total,
    compareTotal,
    saving: compareTotal - total,
  };
}

export class SkinEliteGripProduct {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('[data-grip-form]');
    this.variantInput = section.querySelector('[data-grip-variant-id]');
    this.quantityInput = section.querySelector('[data-grip-quantity]');
    this.variants = JSON.parse(section.querySelector('[data-grip-variants]').textContent);
    this.initialVariant = this.variants.find((variant) => String(variant.id) === this.variantInput?.value);
    if (!this.form || !this.initialVariant) return;
    this.pairCount = 1;
    this.pairIds = Array(4).fill(String(this.initialVariant.id));
    this.busy = false;
    this.cartNeedsReview = false;
    this.events = new AbortController();
    this.gallery = section.querySelector('[data-grip-gallery]');
    this.slides = Array.from(section.querySelectorAll('[data-grip-slide]'));
    this.thumbnails = Array.from(section.querySelectorAll('[data-grip-thumbnail]'));
    this.currency = section.dataset.currency || 'USD';
    try {
      this.moneyFormatter = new Intl.NumberFormat(section.dataset.locale || 'en', { style: 'currency', currency: this.currency });
    } catch {
      this.moneyFormatter = new Intl.NumberFormat('en', { style: 'currency', currency: 'USD' });
    }
    this.bindSelection();
    this.bindGallery();
    this.bindZoom();
    this.bindSticky();
    this.syncSelection();
    section.querySelector('[data-grip-controls]').hidden = false;
    section.querySelector('[data-grip-fallback]').hidden = true;
    section.querySelector('[data-grip-thumbnails]').hidden = false;
    section.querySelector('[data-grip-gallery-controls]').hidden = false;
    section.dataset.gripInitialized = 'true';
    if (new URL(window.location.href).searchParams.has('variant')) {
      requestAnimationFrame(() => this.focusVariantImage(this.initialVariant));
    }
  }

  listen(element, type, callback, options = {}) {
    element?.addEventListener(type, callback, { ...options, signal: this.events.signal });
  }

  formatMoney(cents, withCurrency = false) {
    const formatted = this.moneyFormatter.format(cents / 100);
    return withCurrency ? `${formatted} ${this.currency}` : formatted;
  }

  getSelection() {
    return buildGripSelection(this.variants, this.pairIds, this.pairCount);
  }

  bindSelection() {
    this.listen(this.form, 'submit', (event) => {
      event.preventDefault();
      this.addToCart();
    });
    this.section.querySelectorAll('[data-grip-pair-count]').forEach((input) => {
      this.listen(input, 'change', () => {
        if (this.busy || !input.checked) return;
        this.pairCount = Number(input.value);
        this.syncSelection();
      });
    });
    this.section.querySelectorAll('[data-grip-pair-variant]').forEach((select) => {
      this.listen(select, 'change', () => {
        if (this.busy) return;
        const index = Number(select.dataset.gripPairVariant);
        this.pairIds[index] = select.value;
        this.syncSelection();
        const variant = this.variants.find((item) => String(item.id) === select.value);
        if (variant) this.focusVariantImage(variant);
        if (index === 0 && variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState(window.history.state, '', url);
        }
      });
    });
    this.listen(window, 'popstate', () => {
      if (this.busy) return;
      const id = new URL(window.location.href).searchParams.get('variant') || String(this.initialVariant.id);
      const variant = this.variants.find((item) => String(item.id) === id);
      if (!variant) return;
      this.pairIds[0] = String(variant.id);
      this.syncSelection();
      this.focusVariantImage(variant);
    });
  }

  syncSelection() {
    const selection = this.getSelection();
    this.section.querySelectorAll('[data-grip-offer-price]').forEach((element) => {
      const offer = buildGripSelection(this.variants, this.pairIds, Number(element.dataset.gripOfferPrice));
      element.textContent = offer.error ? '—' : this.formatMoney(offer.total);
    });
    this.section.querySelectorAll('[data-grip-pair-count]').forEach((input) => { input.checked = Number(input.value) === this.pairCount; });
    this.section.querySelectorAll('[data-grip-pair-row]').forEach((row, index) => {
      row.hidden = index >= this.pairCount;
      const select = row.querySelector('[data-grip-pair-variant]');
      select.disabled = index >= this.pairCount;
      select.value = this.pairIds[index];
      const variant = this.variants.find((item) => String(item.id) === this.pairIds[index]);
      row.querySelector('[data-grip-pair-price]').textContent = variant ? this.formatMoney(variant.price) : '—';
      this.setImage(row.querySelector('[data-grip-pair-image]'), variant?.image);
    });
    this.variantInput.value = this.pairIds[0];
    this.quantityInput.value = String(this.pairCount);
    this.section.querySelectorAll('[data-grip-total]').forEach((element) => {
      element.textContent = selection.error ? '—' : this.formatMoney(selection.total, true);
    });
    const comparison = this.section.querySelector('[data-grip-compare]');
    const saving = this.section.querySelector('[data-grip-saving]');
    comparison.hidden = saving.hidden = Boolean(selection.error || selection.saving <= 0);
    comparison.textContent = this.formatMoney(selection.compareTotal);
    saving.textContent = selection.saving > 0 ? `Save ${this.formatMoney(selection.saving)}` : '';
    this.section.querySelector('[data-grip-summary-label]').textContent = `${this.pairCount} ${this.pairCount === 1 ? 'item' : 'items'} total`;
    const colours = selection.variants.map((variant) => variant.colour);
    const uniqueColours = [...new Set(colours)];
    this.section.querySelector('[data-grip-sticky-selection]').textContent = `${this.pairCount} ${this.pairCount === 1 ? 'item' : 'items'} · ${uniqueColours.join(' / ') || 'Choose colours'}`;
    this.setImage(this.section.querySelector('[data-grip-sticky-image]'), selection.variants[0]?.image);
    this.setButtonState();
    if (!this.cartNeedsReview) this.setStatus(selection.error || '', Boolean(selection.error));
  }

  setImage(image, source) {
    if (!image || !source || image.getAttribute('src') === source) return;
    image.removeAttribute('srcset');
    image.src = source;
  }

  setButtonState() {
    const invalid = Boolean(this.getSelection().error);
    this.section.querySelectorAll('[data-grip-add]').forEach((button) => {
      button.disabled = this.busy || this.cartNeedsReview || invalid;
      button.setAttribute('aria-busy', String(this.busy));
      button.textContent = this.busy ? 'Adding…' : this.cartNeedsReview ? 'Check your cart' : invalid ? 'Choose available colours' : 'Add to cart';
    });
    this.section.querySelectorAll('[data-grip-selection-controls]').forEach((fieldset) => { fieldset.disabled = this.busy; });
    this.form.setAttribute('aria-busy', String(this.busy));
  }

  setStatus(message, isError = false) {
    const status = this.section.querySelector('[data-grip-status]');
    status.textContent = message;
    status.classList.toggle('is-error', isError);
    status.setAttribute('role', isError ? 'alert' : 'status');
    status.setAttribute('aria-live', isError ? 'assertive' : 'polite');
    const stickyStatus = this.section.querySelector('[data-grip-sticky-status]');
    stickyStatus.textContent = isError ? message : '';
    stickyStatus.hidden = !isError;
    this.section.querySelector('[data-grip-cart-check]').hidden = !this.cartNeedsReview;
  }

  galleryIndex() {
    if (!this.slides.length) return 0;
    const offset = this.slides[0].offsetLeft;
    return this.slides.reduce((closest, slide, index) => Math.abs(slide.offsetLeft - offset - this.gallery.scrollLeft) < Math.abs(this.slides[closest].offsetLeft - offset - this.gallery.scrollLeft) ? index : closest, 0);
  }

  showSlide(index, focusThumbnail = false, instant = false) {
    const nextIndex = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[nextIndex];
    if (!slide) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.gallery.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: instant || reducedMotion ? 'auto' : 'smooth' });
    this.updateGalleryControls(nextIndex);
    if (focusThumbnail) this.thumbnails[nextIndex]?.focus({ preventScroll: true });
  }

  updateGalleryControls(index = this.galleryIndex()) {
    this.thumbnails.forEach((button, buttonIndex) => {
      button.classList.toggle('is-active', buttonIndex === index);
      button.setAttribute('aria-current', String(buttonIndex === index));
    });
    this.section.querySelector('[data-grip-gallery-count]').textContent = `${index + 1} / ${this.slides.length}`;
    this.section.querySelector('[data-grip-gallery-prev]').disabled = index === 0;
    this.section.querySelector('[data-grip-gallery-next]').disabled = index >= this.slides.length - 1;
  }

  focusVariantImage(variant) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(variant.mediaId));
    if (index >= 0) this.showSlide(index, false, true);
  }

  bindGallery() {
    this.thumbnails.forEach((button, index) => this.listen(button, 'click', () => this.showSlide(index)));
    this.listen(this.section.querySelector('[data-grip-gallery-prev]'), 'click', () => this.showSlide(this.galleryIndex() - 1));
    this.listen(this.section.querySelector('[data-grip-gallery-next]'), 'click', () => this.showSlide(this.galleryIndex() + 1));
    const handleKeys = (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const thumbnail = event.target.closest('[data-grip-thumbnail]');
      const current = thumbnail ? Number(thumbnail.dataset.gripThumbnail) : this.galleryIndex();
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1);
      this.showSlide(index, Boolean(thumbnail));
    };
    this.listen(this.gallery, 'keydown', handleKeys);
    this.listen(this.section.querySelector('[data-grip-thumbnails]'), 'keydown', handleKeys);
    this.listen(this.gallery, 'scroll', () => {
      if (this.galleryFrame) return;
      this.galleryFrame = requestAnimationFrame(() => { this.galleryFrame = null; this.updateGalleryControls(); });
    }, { passive: true });
    this.listen(window, 'resize', () => this.updateGalleryControls(), { passive: true });
    this.updateGalleryControls(0);
  }

  bindZoom() {
    const dialog = this.section.querySelector('[data-grip-zoom-dialog]');
    const viewport = this.section.querySelector('[data-grip-zoom-viewport]');
    const image = this.section.querySelector('[data-grip-zoom-image]');
    const toggle = this.section.querySelector('[data-grip-zoom-toggle]');
    this.section.querySelectorAll('[data-grip-zoom-open]').forEach((trigger) => {
      this.listen(trigger, 'click', (event) => {
        if (typeof dialog.showModal !== 'function') return;
        event.preventDefault();
        this.zoomReturnFocus = trigger;
        image.src = trigger.href;
        image.alt = trigger.querySelector('img')?.alt || 'Product image';
        viewport.classList.remove('is-zoomed');
        toggle.setAttribute('aria-pressed', 'false');
        toggle.textContent = 'Zoom in';
        dialog.showModal();
        viewport.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
    });
    this.listen(toggle, 'click', () => {
      const zoomed = viewport.classList.toggle('is-zoomed');
      toggle.setAttribute('aria-pressed', String(zoomed));
      toggle.textContent = zoomed ? 'Zoom out' : 'Zoom in';
    });
    this.listen(this.section.querySelector('[data-grip-zoom-close]'), 'click', () => dialog.close());
    this.listen(dialog, 'click', (event) => {
      const bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
    });
    this.listen(dialog, 'close', () => this.zoomReturnFocus?.focus({ preventScroll: true }));
  }

  bindSticky() {
    const update = () => {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        const button = this.form.querySelector('[data-grip-add]');
        const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
        const drawerOpen = Boolean(document.querySelector('theme-drawer[open], dialog[open]:not([data-grip-zoom-dialog])'));
        const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
        const zoomOpen = this.section.querySelector('[data-grip-zoom-dialog]').open;
        this.section.querySelector('[data-grip-sticky]').hidden = button.getBoundingClientRect().bottom >= 0 || Boolean(footerVisible) || drawerOpen || zoomOpen;
      });
    };
    this.listen(window, 'scroll', update, { passive: true });
    this.listen(document.querySelector('.page-wrapper'), 'scroll', update, { passive: true });
    this.listen(window, 'resize', update, { passive: true });
    this.listen(document, 'theme-drawer:open', update);
    this.listen(document, 'theme-drawer:close', update);
    this.listen(this.section, 'click', update);
    this.listen(this.section.querySelector('[data-grip-zoom-dialog]'), 'close', update);
    update();
  }

  cartRoutes() {
    const routes = typeof Theme !== 'undefined' ? Theme.routes : null;
    const root = window.Shopify?.routes?.root || '/';
    return { add: routes?.cart_add_url || `${root}cart/add.js`, cart: `${routes?.cart_url || `${root}cart`}.js`, page: routes?.cart_url || `${root}cart` };
  }

  async fetchCart() {
    const response = await fetch(this.cartRoutes().cart, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Your cart could not be refreshed.');
    return response.json();
  }

  async addToCart() {
    if (this.busy || this.cartNeedsReview) return;
    const selection = this.getSelection();
    if (selection.error) {
      this.setStatus(selection.error, true);
      this.section.querySelector(`[data-grip-pair-variant="${selection.invalidIndex}"]`)?.focus();
      return;
    }
    this.busy = true;
    this.setButtonState();
    this.setStatus('Adding your selection to cart…');
    const sections = Array.from(document.querySelectorAll('cart-items-component')).map((component) => component.dataset.sectionId).filter((id, index, ids) => id && ids.indexOf(id) === index);
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    const detail = { source: 'skin-elite-grip', sourceId: this.form.id, itemCount: this.pairCount, productId: this.section.dataset.productId };
    this.form.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: selection.items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })), promise: deferred.promise }));
    let wasAdded = false;
    let rejected = false;
    try {
      const response = await fetch(this.cartRoutes().add, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: selection.items, sections: sections.join(','), sections_url: window.location.pathname }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status) {
        rejected = response.status >= 400 && response.status < 500;
        throw new Error(payload.description || payload.message || 'These items could not be added. Please check availability.');
      }
      wasAdded = true;
      const cart = await this.fetchCart();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, items: cart.items, sections: payload.sections, didError: false } });
      this.setStatus(`${this.pairCount} ${this.pairCount === 1 ? 'item' : 'items'} added to cart.`);
      if (!document.querySelector('cart-drawer-component[auto-open]')) window.location.assign(this.cartRoutes().page);
    } catch (error) {
      // An accepted or uncertain POST must never be repeated automatically.
      this.cartNeedsReview = true;
      const message = wasAdded
        ? 'Your items were added, but the cart display could not refresh. Check your cart before adding again.'
        : rejected
          ? `${error.message} Check your cart before adding again.`
          : 'We could not confirm the cart update. Check your cart before adding again.';
      this.form.dispatchEvent(new CartErrorEvent({ error: message, code: rejected ? 'INVALID' : 'SERVICE_UNAVAILABLE', detail: { description: message } }));
      try {
        const cart = await this.fetchCart();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, items: cart.items, didError: true } });
      } catch (refreshError) {
        deferred.reject(refreshError);
      }
      this.setStatus(message, true);
      this.section.querySelector(this.cartNeedsReview ? '[data-grip-cart-check]' : '[data-grip-status]')?.focus({ preventScroll: false });
    } finally {
      this.busy = false;
      this.setButtonState();
    }
  }

  destroy() {
    this.events.abort();
    if (this.galleryFrame) cancelAnimationFrame(this.galleryFrame);
    if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
    this.section.querySelector('[data-grip-zoom-dialog]')?.close();
    delete this.section.gripProduct;
  }
}

function initializeGripProducts(scope = document) {
  const sections = scope.matches?.('[data-grip-product]') ? [scope] : scope.querySelectorAll('[data-grip-product]');
  sections.forEach((section) => {
    if (section.gripProduct) return;
    try { section.gripProduct = new SkinEliteGripProduct(section); } catch (error) { console.warn('[SKIN ELITE CO. grip] Product controls could not initialize.', error); }
  });
}

if (typeof document !== 'undefined') {
  initializeGripProducts();
  document.addEventListener('shopify:section:load', (event) => initializeGripProducts(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-grip-product]').forEach((section) => section.gripProduct?.destroy()));
}
